import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Package, CreditCard, MessageCircle } from 'lucide-react';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;
  const paymentMethod = location.state?.paymentMethod || 'Razorpay';
  const paymentId = location.state?.paymentId;
  const advancePaid = location.state?.advancePaid;
  const balanceDue = location.state?.balanceDue;

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi Studio Crochet, I have placed an order on your website.\nOrder ID: ${order?.id}\nName: ${order?.customer_name}\nPlease share tracking details.`
    );
    window.open(`https://wa.me/919999633121?text=${message}`, '_blank');
  };

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <p className="font-outfit text-[#8C8577] mb-4">No order found</p>
          <button onClick={() => navigate('/')} className="bg-[#C29B57] text-white px-6 py-2 rounded-md hover:bg-[#99733D] transition-colors" data-testid="go-home-button">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FCFBF8]" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <CheckCircle className="w-20 h-20 text-[#3A5A40] mx-auto mb-4" />
          <h1 className="font-cormorant text-4xl sm:text-5xl mb-4" data-testid="confirmation-heading">Order Confirmed!</h1>
          <p className="font-outfit text-lg text-[#8C8577]">Thank you for shopping with Studio Crochet</p>
        </div>

        {/* Order Details */}
        <div className="bg-white p-8 rounded-lg shadow-sm" data-testid="order-details">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#EBE4D5]">
            <div>
              <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577]">Order ID</p>
              <p className="font-outfit text-lg" data-testid="order-id">{order.id?.slice(0, 12)}...</p>
            </div>
            <div className="text-right">
              <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577]">Status</p>
              <p className="font-outfit text-lg capitalize text-[#3A5A40]" data-testid="order-status">{order.status}</p>
            </div>
          </div>

          {/* Payment Method */}
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#EBE4D5]">
            {paymentMethod === 'COD' ? (
              <Package className="w-5 h-5 text-[#C29B57]" />
            ) : (
              <CreditCard className="w-5 h-5 text-[#C29B57]" />
            )}
            <div>
              <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577]">Payment Method</p>
              <p className="font-outfit" data-testid="payment-method">
                {paymentMethod === 'COD' ? 'Cash on Delivery' : 'Paid via Razorpay'}
              </p>
              {paymentId && <p className="font-outfit text-xs text-[#8C8577]">Payment ID: {paymentId}</p>}
            </div>
          </div>

          {/* COD Advance / Balance Breakdown */}
          {paymentMethod === 'COD' && advancePaid > 0 && (
            <div className="mb-6 pb-6 border-b border-[#EBE4D5] bg-[#F3EFE6] p-4 rounded-md" data-testid="cod-payment-breakdown">
              <p className="font-outfit text-sm font-medium text-[#1A1A18] mb-3">Payment Breakdown</p>
              <div className="space-y-2">
                <div className="flex justify-between font-outfit text-sm"><span>Total Order Amount</span><span>₹{order.total?.toLocaleString()}</span></div>
                <div className="flex justify-between font-outfit text-sm text-[#3A5A40] font-medium"><span>Advance Paid (Online)</span><span>₹{advancePaid?.toLocaleString()}</span></div>
                <div className="flex justify-between font-outfit text-sm text-[#C29B57] font-medium"><span>Balance Due on Delivery</span><span>₹{balanceDue?.toLocaleString()}</span></div>
              </div>
            </div>
          )}

          {/* Items */}
          <div className="mb-6 pb-6 border-b border-[#EBE4D5]">
            <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-4">Items Ordered</p>
            <div className="space-y-3">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between font-outfit text-sm" data-testid={`confirmation-item-${idx}`}>
                  <span>{item.product_name} x {item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-between font-outfit text-xl mb-6">
            <span>Total</span>
            <span className="text-[#C29B57]" data-testid="confirmation-total">₹{order.total?.toLocaleString()}</span>
          </div>

          {/* Delivery Info */}
          <div className="mb-6 pb-6 border-b border-[#EBE4D5]">
            <p className="font-outfit text-sm uppercase tracking-wider text-[#8C8577] mb-2">Delivery Address</p>
            <p className="font-outfit text-base" data-testid="delivery-address">{order.customer_address}</p>
          </div>

          {/* Tracking Info */}
          <div className="bg-[#F3EFE6] p-5 rounded-lg mb-6" data-testid="tracking-info">
            <p className="font-outfit text-sm font-medium text-[#1A1A18] mb-2">Order Tracking</p>
            <p className="font-outfit text-sm text-[#4A463F]">
              Tracking details will be shared personally via WhatsApp or Email. Our team will reach out to you shortly.
            </p>
          </div>

          {/* WhatsApp Confirmation Button */}
          <button
            onClick={openWhatsApp}
            className="w-full bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors duration-300 rounded-md px-8 py-4 font-outfit text-sm flex items-center justify-center gap-3 mb-4"
            data-testid="whatsapp-confirm-button"
          >
            <MessageCircle className="w-5 h-5" />
            Confirm Order on WhatsApp
          </button>

          {/* Contact */}
          <div className="bg-[#F3EFE6] p-4 rounded-lg">
            <p className="font-outfit text-sm text-[#4A463F]">
              For any queries, contact us at <a href="tel:9999633121" className="text-[#C29B57] hover:underline">9999633121</a> or <a href="mailto:ekta.verma320@gmail.com" className="text-[#C29B57] hover:underline">ekta.verma320@gmail.com</a>
            </p>
          </div>
        </div>

        {/* Continue Shopping */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/shop')}
            className="bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-8 py-4 font-outfit uppercase tracking-wider text-sm"
            data-testid="continue-shopping-button"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
